import type { PaginationMetadata, PaginationQuery } from './common'
import type { RawMaterialKind } from './raw-material'

export const STOCK_BUCKETS = ['on_hand', 'repacking', 'rider'] as const
export type StockBucket = (typeof STOCK_BUCKETS)[number]

export const STOCK_BUCKET_LABELS: Record<StockBucket, string> = {
  on_hand: 'Di Toko (Utuh)',
  repacking: 'Di Bar (Terbuka)',
  rider: 'Dibawa Rider',
}

export const STOCK_REFERENCE_TYPES = [
  'expense',
  'sale',
  'rider_sale',
  'opname',
  'adjustment',
  'transformation',
  'opening',
] as const
export type StockReferenceType = (typeof STOCK_REFERENCE_TYPES)[number]

export const STOCK_REFERENCE_TYPE_LABELS: Record<StockReferenceType, string> = {
  expense: 'Pengeluaran / Belanja',
  sale: 'Penjualan Kasir',
  rider_sale: 'Penjualan Rider',
  opname: 'Stock Opname',
  adjustment: 'Penyesuaian Stok',
  transformation: 'Transformasi Stok',
  opening: 'Stok Awal',
}

export const STOCK_REASON_DIRECTIONS = ['plus', 'minus', 'both'] as const
export type StockReasonDirection = (typeof STOCK_REASON_DIRECTIONS)[number]

export interface StockReason {
  id: string
  workspaceId: string
  code: string
  name: string
  direction: StockReasonDirection
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateStockReasonReq {
  code: string
  name: string
  direction: StockReasonDirection
  isActive?: boolean
  workspaceId?: string
}

export interface UpdateStockReasonReq {
  id?: string
  code?: string
  name?: string
  direction?: StockReasonDirection
  isActive?: boolean
}

export const STOCK_STATUSES = [
  'in_stock',
  'low_stock',
  'out_of_stock',
] as const

export type StockStatus = (typeof STOCK_STATUSES)[number]

export const STOCK_ITEM_TYPES = ['product', 'raw_material'] as const

export type StockItemType = (typeof STOCK_ITEM_TYPES)[number]

export interface StockItemSummary {
  id: string
  itemId: string
  name: string
  sku: string | null
  itemType: StockItemType
  kind?: RawMaterialKind | null
  categoryName: string
  uomName: string
  onHandQty: number
  repackingQty: number
  riderQty: number
  totalStock: number
  minStock: number
  costPerUnit: number
  totalValue: number
  status: StockStatus
}

export interface StockMovement {
  id: string
  workspaceId: string
  itemType: 'product' | 'raw_material'
  itemId: string
  uomId: string
  qty: number
  bucketFrom: StockBucket | null
  riderIdFrom: string | null
  bucketTo: StockBucket | null
  riderIdTo: string | null
  unitCost: number | null
  reasonId: string | null
  reasonName?: string | null
  referenceType: StockReferenceType | null
  referenceId: string | null
  note: string | null
  createdBy: string | null
  createdByName?: string | null
  createdAt: string
}

export interface StockCardEntry {
  id: string
  /** Quantity moved by the movement itself (a bucket transfer has qtyIn = qtyOut = 0). */
  qty: number
  date: string
  referenceType: StockReferenceType | null
  referenceId: string | null
  referenceCode?: string | null
  bucketFrom: StockBucket | null
  riderIdFrom: string | null
  bucketTo: StockBucket | null
  riderIdTo: string | null
  qtyIn: number
  qtyOut: number
  runningBalance: number
  unitCost: number | null
  reasonName: string | null
  note: string | null
  createdByName: string | null
}

export interface StockKpiMetrics {
  totalItems: number
  inStockCount: number
  lowStockCount: number
  outOfStockCount: number
  totalAssetValue: number
}

export interface GetStockListReq {
  q?: string
  type?: 'all' | 'product' | 'raw_material'
  kind?: RawMaterialKind | 'all'
  status?: 'all' | StockStatus
  category?: string
  pagination?: PaginationQuery
}

export interface StockListRes {
  items: StockItemSummary[]
  pagination: PaginationMetadata
}

export interface GetStockCardReq {
  itemType: 'product' | 'raw_material'
  itemId: string
  bucket?: StockBucket
  fromDate?: string
  toDate?: string
  pagination?: PaginationQuery
}

export interface StockCardRes {
  item: {
    id: string
    name: string
    sku: string | null
    itemType: 'product' | 'raw_material'
    uomName: string
  }
  initialBalance: number
  finalBalance: number
  entries: StockCardEntry[]
  pagination: PaginationMetadata
}
